import { z } from 'zod';

import { MessageAuthorSchema } from '../conversation/message_author.js';

/**
 * "Reportar problema" no app (voll-specs 2026-09-29-app-problem-reports, índice dos planos
 * §Contrato). Três bordas usam estes schemas:
 *  - web-chat ↔ app-integration (`/problem-reports`, respostas `{ v: 1, … }`);
 *  - app-integration → support agent (`POST /internal/app/reports`) e os callbacks de volta
 *    (`POST /internal/problem-reports/replies`) — o support agent vive em outro
 *    repositório e testa contra a MESMA fixture (`test-fixtures/problem_report/app_contract.json`);
 *  - o frame SSE `{ v: 1, problemReport }` do `/stream`, distinto por ESTRUTURA dos irmãos
 *    (`messages`/`resolutions`/`notifications`), no molde de `AppChatNotificationsPushSchema`.
 */

/** Id Mongo (ObjectId hex minúsculo) de report, mensagem e anexo. */
export const ProblemReportIdSchema = z.string().regex(/^[0-9a-f]{24}$/);

export const AppReportPhaseSchema = z.enum(['collecting', 'analyzing', 'answered']);

/** Quem escreve num report do app: o viajante ou o support agent — subconjunto do
 *  vocabulário canônico, nunca literais novos. */
const AppReportAuthorSchema = MessageAuthorSchema.extract(['customer', 'agent']);

export const PROBLEM_REPORT_MESSAGE_MAX_CHARS = 4_000;
export const PROBLEM_REPORT_MAX_ATTACHMENTS = 5;
/** Teto do print: o web-chat recusa antes de enviar, o BFF antes da rede, o support
 *  agent confere de novo. */
export const PROBLEM_REPORT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ProblemReportAttachmentContentTypeSchema = z.enum([
  'image/png',
  'image/jpeg',
  'image/webp',
]);

const AppReportMessageSchema = z
  .object({
    id: ProblemReportIdSchema,
    author: AppReportAuthorSchema,
    text: z.string(),
    /** Só nas mensagens do viajante; `[]` nas do agente. */
    attachmentIds: z.array(ProblemReportIdSchema),
    createdAt: z.iso.datetime(),
  })
  .strict();
export type AppReportMessage = z.infer<typeof AppReportMessageSchema>;

export const AppReportViewSchema = z
  .object({
    reportId: ProblemReportIdSchema,
    phase: AppReportPhaseSchema,
    messages: z.array(AppReportMessageSchema),
  })
  .strict();
export type AppReportView = z.infer<typeof AppReportViewSchema>;

/** Contexto automático (errata E7): rota de origem, relógio do aparelho, locale e host. */
export const AppReportContextSchema = z
  .object({
    fromRoute: z.string().min(1).max(512).nullable(),
    occurredAt: z.iso.datetime(),
    locale: z.string().min(2).max(35),
    host: z.enum(['native', 'iframe', 'web']),
  })
  .strict();
export type AppReportContext = z.infer<typeof AppReportContextSchema>;

// ── web-chat → app-integration ──────────────────────────────────────────────
export const OpenProblemReportBodySchema = z.object({ context: AppReportContextSchema }).strict();

export const ProblemReportMessageBodySchema = z
  .object({
    text: z.string().min(1).max(PROBLEM_REPORT_MESSAGE_MAX_CHARS),
    attachmentIds: z.array(ProblemReportIdSchema).max(PROBLEM_REPORT_MAX_ATTACHMENTS),
  })
  .strict();
export type ProblemReportMessageBody = z.infer<typeof ProblemReportMessageBodySchema>;

// ── app-integration → web-chat ──────────────────────────────────────────────
export const ProblemReportEligibilityResponseSchema = z
  .object({ v: z.literal(1), enabled: z.boolean() })
  .strict();
export type ProblemReportEligibilityResponse = z.infer<
  typeof ProblemReportEligibilityResponseSchema
>;

export const ProblemReportOpenedResponseSchema = z
  .object({ v: z.literal(1), reportId: ProblemReportIdSchema })
  .strict();
export type ProblemReportOpenedResponse = z.infer<typeof ProblemReportOpenedResponseSchema>;

export const ProblemReportAttachmentResponseSchema = z
  .object({ v: z.literal(1), attachmentId: ProblemReportIdSchema })
  .strict();
export type ProblemReportAttachmentResponse = z.infer<typeof ProblemReportAttachmentResponseSchema>;

export const ProblemReportMessageAcceptedResponseSchema = z
  .object({ v: z.literal(1), messageId: ProblemReportIdSchema })
  .strict();
export type ProblemReportMessageAcceptedResponse = z.infer<
  typeof ProblemReportMessageAcceptedResponseSchema
>;

export const ProblemReportResponseSchema = z
  .object({ v: z.literal(1), report: AppReportViewSchema })
  .strict();
export type ProblemReportResponse = z.infer<typeof ProblemReportResponseSchema>;

export const ProblemReportLatestResponseSchema = z
  .object({ v: z.literal(1), report: AppReportViewSchema.nullable() })
  .strict();
export type ProblemReportLatestResponse = z.infer<typeof ProblemReportLatestResponseSchema>;

// ── app-integration → support agent (canal app) ─────────────────────────────
/** Identidade como FATO do sistema (invariante I4): `publicId` (UUIDv5), nome, empresa e cliente do
 *  workspace efetivo. O `userId` legado nunca entra aqui. */
export const InternalOpenAppReportBodySchema = z
  .object({
    publicId: z.uuid(),
    name: z.string().nullable(),
    companyName: z.string().nullable(),
    /** Cliente do workspace efetivo (`business.clients.id`); `null` sem cliente. */
    clientId: z.string().nullable(),
    context: AppReportContextSchema,
  })
  .strict();
export type InternalOpenAppReportBody = z.infer<typeof InternalOpenAppReportBodySchema>;

/** Título guardado de um envio (spec 2026-10-19-meus-envios-titulo-da-issue, revisão de 23/10):
 *  o app-integration gera com o modelo barato e guarda no conversation
 *  (`/app-chat/problem-report-titles`), porque o support agent não guarda o título.
 *  `title: null` = ainda sem pedido claro (só cumprimento ou algo vago); `attemptedAt` é quando
 *  a última tentativa terminou, para só tentar de novo depois de o envio mudar. */
export const ProblemReportTitleRecordSchema = z
  .object({
    reportId: ProblemReportIdSchema,
    title: z.string().min(1).nullable(),
    attemptedAt: z.iso.datetime(),
  })
  .strict();
export type ProblemReportTitleRecord = z.infer<typeof ProblemReportTitleRecordSchema>;

/** `GET /app-chat/problem-report-titles?sub=…&ids=…`: só os envios que já têm registro. */
export const ProblemReportTitlesResponseSchema = z
  .object({ titles: z.array(ProblemReportTitleRecordSchema) })
  .strict();

/** `PUT /app-chat/problem-report-titles/:reportId`. Um título não nulo já guardado nunca é
 *  trocado; um `null` guardado é. */
export const SaveProblemReportTitleBodySchema = z
  .object({
    sub: z.string().min(1).max(200),
    title: z.string().min(1).max(120).nullable(),
  })
  .strict();

// ── support agent → app-integration → web-chat ──────────────────────────────
export const AppProblemReportReplySchema = z
  .object({
    sub: z.string().min(1),
    reportId: ProblemReportIdSchema,
    phase: AppReportPhaseSchema,
    message: AppReportMessageSchema,
  })
  .strict();

export const AppProblemReportPushSchema = z
  .object({ v: z.literal(1), problemReport: AppReportViewSchema })
  .strict();
export type AppProblemReportPush = z.infer<typeof AppProblemReportPushSchema>;

/** Andamento do envio (spec 2026-10-11-feedback-progress): só os dois marcos que o viajante vê. */
const ProblemReportProgressSchema = z.enum(['in_development', 'published']);

/** Item da lista "Meus relatos" (spec 2026-09-30-problem-report-history): data, primeira frase
 *  do viajante (`preview`, `null` sem mensagem dele) e fase — sem desfecho/categoria;
 *  `progress` é o andamento (spec 2026-10-11-feedback-progress) e `unread` diz se há mensagem
 *  do suporte que o viajante ainda não viu (spec 2026-10-17-feedback-unread-badges), os dois
 *  opcionais na entrada porque o support agent só os manda com `?progress=1` e `?unread=1`.
 *  `title` é o título semântico do envio, gerado pelo modelo barato no idioma da conversa (spec
 *  2026-10-19-meus-envios-titulo-da-issue), só com `?title=1`; o BFF o preenche com o título
 *  guardado no conversation. Sem `.max` de propósito (um teto em unidades UTF-16 derrubaria a
 *  lista com um título de emoji) e título vazio ou só espaços vira `null` em vez de recusar a
 *  lista inteira —
 *  `preview` segue sendo a primeira frase do viajante e o fallback do card. É também o item do
 *  corpo `{ reports }` do support agent (`GET /internal/app/reports`). */
export const ProblemReportSummarySchema = z
  .object({
    reportId: ProblemReportIdSchema,
    phase: AppReportPhaseSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    preview: z.string().nullable(),
    progress: ProblemReportProgressSchema.nullable().default(null),
    unread: z.boolean().default(false),
    title: z
      .string()
      .nullable()
      .transform((value) => (value?.trim() ? value : null))
      .default(null),
  })
  .strict();
export type ProblemReportSummary = z.infer<typeof ProblemReportSummarySchema>;

/** `GET /problem-reports`: os 20 mais recentes por `updatedAt` (ordem do support agent). */
export const ProblemReportListResponseSchema = z
  .object({ v: z.literal(1), reports: z.array(ProblemReportSummarySchema) })
  .strict();
export type ProblemReportListResponse = z.infer<typeof ProblemReportListResponseSchema>;

/** `GET /problem-reports/unread` (spec 2026-10-17-feedback-unread-badges): há mensagem do
 *  suporte que o viajante ainda não viu, em qualquer envio dele? Booleano, nunca contagem. O
 *  support agent devolve `{ unread }` e o BFF embrulha em `{ v: 1, unread }`. */
export const ProblemReportUnreadResponseSchema = z
  .object({ v: z.literal(1), unread: z.boolean() })
  .strict();
export type ProblemReportUnreadResponse = z.infer<typeof ProblemReportUnreadResponseSchema>;
